// Site interaction instrumentation for GA. One delegated listener pair
// (click + change) maps the app's stable element ids and data attributes to
// snake_case GA events with small flat params, so feature modules stay free
// of analytics calls. Also wires the My Account debug-overlay toggle.
import {
  analyticsOverlayEnabled,
  fireOncePerSession,
  setAnalyticsOverlayEnabled,
  subscribeAnalyticsOverlayEnabled,
  track,
} from "./analytics.js";

type ClickRule = {
  selector: string;
  event: string;
  params?: (element: HTMLElement) => Record<string, string | number | boolean>;
  oncePerSession?: boolean;
};

const CLICK_RULES: ClickRule[] = [
  // My Account
  {
    selector: "[data-account-tab]",
    event: "account_tab_switch",
    params: (element) => ({ tab: element.dataset.accountTab ?? "" }),
  },
  { selector: "#accountSignOutBtn", event: "sign_out_open" },
  { selector: "#accountSignOutDialogConfirm", event: "sign_out_confirm" },
  // AI request packs (My Account > Plan). Purchase completion itself is
  // server-truth (webhook); GA sees the funnel plus the checkout return below.
  {
    selector: "button[data-ai-pack-buy]",
    event: "ai_pack_buy_open",
    params: (element) => ({ pack: element.dataset.aiPackBuy ?? "" }),
  },
  { selector: "#accountAiPackCheckoutBtn", event: "ai_pack_checkout_continue" },
  { selector: "#accountAiPackPayBar .account-checkout-pay", event: "ai_pack_pay_submit" },
  { selector: "#accountAiPackDialogClose", event: "ai_pack_dialog_dismiss" },
  // Admin
  {
    selector: "[data-admin-tab]",
    event: "admin_tab_switch",
    params: (element) => ({ tab: element.dataset.adminTab ?? "" }),
  },
  // Dashboard
  {
    selector: "[data-health-card]",
    event: "health_card_open",
    params: (element) => ({ card: element.dataset.healthCard ?? "" }),
  },
  {
    selector: ".sensitivity-option[data-sensitivity]",
    event: "sensitivity_change",
    params: (element) => ({ level: element.dataset.sensitivity ?? "" }),
  },
  { selector: "[data-engine-scope-card]", event: "engine_scope_switch" },
  // Engine panel scope strip: Aircraft / Engine (or per-engine) / Cyl N.
  {
    selector: "[data-scope-cylinder]",
    event: "cylinder_scope_select",
    params: (element) => ({ cylinder: element.dataset.scopeCylinder ?? "" }),
  },
  {
    selector: "[data-scope-engine-id]",
    event: "engine_scope_select",
    params: (element) => ({ engine: element.dataset.scopeEngineId ?? "" }),
  },
  { selector: "[data-scope-engine]", event: "engine_scope_select", params: () => ({ engine: "engine" }) },
  { selector: "[data-scope-aircraft]", event: "engine_scope_select", params: () => ({ engine: "aircraft" }) },
  { selector: "[data-cylinder-chart]", event: "cylinder_chart_open" },
  { selector: "[data-cylinder-clear]", event: "cylinder_focus_clear" },
  { selector: ".flight-menu-option", event: "flight_select", params: () => ({ source: "menu" }) },
  { selector: "[data-ai-summary-empty-action], [data-ai-summary-regenerate]", event: "ai_summary_generate" },
  {
    selector: "[data-gami-question]",
    event: "gami_question_toggle",
    params: (element) => ({ question: element.dataset.gamiQuestion ?? "" }),
  },
  // Trends
  {
    selector: "[data-trend-metric]",
    event: "trend_metric_select",
    params: (element) => ({ metric: element.dataset.trendMetric ?? "" }),
  },
  {
    selector: "[data-trend-visual]",
    event: "trend_visual_select",
    params: (element) => ({ visual: element.dataset.trendVisual ?? "" }),
  },
  {
    selector: "[data-trend-engine]",
    event: "trend_engine_select",
    params: (element) => ({ engine: element.dataset.trendEngine ?? "" }),
  },
  {
    selector: "[data-trend-cylinder]",
    event: "trend_cylinder_select",
    params: (element) => ({ cylinder: element.dataset.trendCylinder ?? "" }),
  },
  { selector: "#trendAnalysisCancel", event: "trend_analysis_cancel" },
  // Uploads page
  { selector: "[data-upload-download]", event: "upload_raw_download" },
  { selector: "[data-upload-reprocess]", event: "upload_reprocess" },
  { selector: "[data-upload-process-short]", event: "upload_process_short" },
  { selector: "[data-upload-delete]", event: "upload_delete_open" },
  { selector: "#aircraftUploadsDeleteSelected", event: "upload_bulk_delete_open" },
  { selector: "[data-aircraft-flight-reprocess]", event: "flight_reprocess" },
  // Footer
  { selector: "#siteVersionPill", event: "version_history_open" },
  { selector: "#action-report", event: "report_pdf_download" },
  { selector: "#action-share-flight, #action-share-flight-compact", event: "flight_share_open" },
  { selector: "#shareLinkDialogCreate, #shareLinkDialogReplace", event: "share_link_create" },
  { selector: "#shareLinkDialogCopy", event: "share_link_copy" },
  { selector: "#shareLinkDialogRevoke", event: "share_link_revoke" },
  {
    selector: ".tab[data-panel]",
    event: "report_tab_switch",
    params: (element) => ({ tab_id: element.dataset.panel ?? "" }),
  },
  {
    selector: "[data-aircraft-panel]",
    event: "aircraft_panel_switch",
    params: (element) => ({ panel: element.dataset.aircraftPanel ?? "" }),
  },
  { selector: "#fleetSidebarToggle", event: "fleet_sidebar_open" },
  { selector: "[data-fleet-collapse]", event: "fleet_sidebar_collapse_toggle" },
  { selector: "[data-fleet-toggle]", event: "sidebar_aircraft_toggle" },
  // After the chevron rule: the chevron sits inside the aircraft head, so the
  // more specific toggle match must win.
  { selector: "[data-fleet-aircraft]", event: "sidebar_aircraft_open" },
  { selector: "[data-fleet-flight]", event: "sidebar_flight_open" },
  {
    selector: "[data-fleet-upload], [data-fleet-upload-first], #dashboardEmptyAircraftUpload, #flightsEmptyAircraftUpload",
    event: "upload_dialog_open",
  },
  { selector: "[data-fleet-add-aircraft]", event: "aircraft_add_open" },
  { selector: "#adminNavButton, [data-fleet-admin]", event: "admin_open" },
  { selector: ".topbar-user, [data-fleet-account]", event: "account_open" },
  { selector: "#uploadDialogSubmit", event: "upload_submit" },
  { selector: "#uploadCapacityDialogConfirm", event: "upload_capacity_confirm" },
  { selector: "#aircraftEditDialogSave", event: "aircraft_save" },
  { selector: "#aircraftEditDialogDelete", event: "aircraft_archive_open" },
  { selector: "#aircraftDeleteDialogConfirm", event: "aircraft_delete_confirm" },
  { selector: "#flightDeleteDialogConfirm", event: "flight_delete_confirm" },
  { selector: "#planLimitDialogPlans, #archiveRestoreCountPlans", event: "plan_limit_view_plans" },
  { selector: "#landingLoginSubmit", event: "sign_in_submit", oncePerSession: true },
  { selector: "#landingSignupSubmit", event: "sign_up_submit", oncePerSession: true },
];

function trackExternalLinkClick(anchor: HTMLAnchorElement) {
  let host = "";
  try {
    host = new URL(anchor.href, globalThis.window?.location?.href).host;
  } catch {
    return;
  }
  if (!host || host === globalThis.window?.location?.host) return;
  track("external_link_click", { link_host: host });
}

function onDelegatedClick(event: Event) {
  const target = event.target as HTMLElement | null;
  if (!target?.closest) return;
  for (const rule of CLICK_RULES) {
    const match = target.closest<HTMLElement>(rule.selector);
    if (!match) continue;
    const fire = () => track(rule.event, rule.params ? rule.params(match) : {});
    if (rule.oncePerSession) fireOncePerSession(`event:${rule.event}`, fire);
    else fire();
    return;
  }
  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (anchor && anchor.target === "_blank") trackExternalLinkClick(anchor);
}

function onDelegatedChange(event: Event) {
  const target = event.target as HTMLElement | null;
  if (!target) return;
  if (target.id === "flightLogSelect" || target.id === "dashboardFlightSelect") {
    track("flight_select", { source: target.id === "flightLogSelect" ? "topbar" : "dashboard" });
  }
  if (target.id === "trendAircraftSelect") track("trend_aircraft_select");
}

// --- Error and warning capture --------------------------------------------
// Mirrors runtime problems into GA (and the debug overlay): uncaught errors,
// unhandled promise rejections, and console.error/warn calls. Messages are
// truncated, immediate repeats are collapsed, and a per-page cap keeps a
// render loop from flooding GA.

const ISSUE_EVENT_LIMIT = 30;
const ISSUE_MESSAGE_LIMIT = 150;
let issueEventCount = 0;
let lastIssueSignature = "";

function trackAppIssue(event: string, message: unknown, source: string) {
  if (issueEventCount >= ISSUE_EVENT_LIMIT) return;
  const trimmed = String(message ?? "").replace(/\s+/g, " ").trim().slice(0, ISSUE_MESSAGE_LIMIT);
  if (!trimmed) return;
  const signature = `${event}:${trimmed}`;
  if (signature === lastIssueSignature) return;
  lastIssueSignature = signature;
  issueEventCount += 1;
  track(event, { message: trimmed, source });
}

function consoleArgsMessage(args: unknown[]): string {
  return args.map((arg) => {
    if (arg instanceof Error) return arg.message;
    if (typeof arg === "object" && arg !== null) {
      try {
        return JSON.stringify(arg);
      } catch {
        return "[object]";
      }
    }
    return String(arg);
  }).join(" ");
}

function wireErrorTracking() {
  const win = globalThis.window;
  if (!win?.addEventListener) return;
  win.addEventListener("error", (event) => {
    const source = event.filename ? `${event.filename.split("/").pop()}:${event.lineno}` : "window";
    trackAppIssue("app_error", event.message, source);
  });
  win.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason instanceof Error ? event.reason.message : event.reason;
    trackAppIssue("app_error", reason, "unhandledrejection");
  });
  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    originalError(...args);
    trackAppIssue("console_error", consoleArgsMessage(args), "console");
  };
  const originalWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    originalWarn(...args);
    trackAppIssue("console_warn", consoleArgsMessage(args), "console");
  };
}

function wireOverlayToggle(doc: Document) {
  // Admin > Analytics > "Show Event Capture Overlay". Static markup, so this
  // wires at boot even though only admins can reach the panel.
  const toggle = doc.querySelector<HTMLInputElement>("#adminAnalyticsOverlayToggle");
  if (!toggle) return;
  toggle.checked = analyticsOverlayEnabled();
  subscribeAnalyticsOverlayEnabled((enabled) => {
    toggle.checked = enabled;
  });
  toggle.addEventListener("change", () => {
    setAnalyticsOverlayEnabled(toggle.checked);
    track("analytics_overlay_toggle", { enabled: toggle.checked });
  });
}

// Stripe redirects land back with a ?billing=<state> marker (subscription
// success/canceled, ai-pack-success/-canceled, portal). No click happens on
// return, so record the outcome once per session at boot.
function trackBillingCheckoutReturn() {
  let state = "";
  try {
    state = new URLSearchParams(globalThis.window?.location?.search ?? "").get("billing") ?? "";
  } catch {
    return;
  }
  if (!state) return;
  fireOncePerSession(`event:billing_checkout_return:${state}`, () => {
    track("billing_checkout_return", { state });
  });
}

export function wireAnalyticsInstrumentation(doc: Document = globalThis.document) {
  if (!doc?.addEventListener) return;
  doc.addEventListener("click", onDelegatedClick);
  doc.addEventListener("change", onDelegatedChange);
  wireErrorTracking();
  wireOverlayToggle(doc);
  trackBillingCheckoutReturn();
}
